import {
  CreateEquipmentModelMutation,
  CreateEquipmentModelMutationVariables,
  EquipmentModel,
  EquipmentModelCreateInput,
  EquipmentModelQuery,
  EquipmentModelQueryVariables,
  EquipmentModelType,
  EquipmentModelUpdateInput,
  UpdateEquipmentModelMutation,
  UpdateEquipmentModelMutationVariables,
} from '__generated__/graphql';
import gql from 'graphql-tag';
import { useOrganization } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { orderBy } from 'lodash';
import { isoTimeStampUtc } from 'utils/dateUtils';

export interface IEquipmentModelFilter {
  equipmentCategoryId?: string;
  equipmentModelType?: EquipmentModelType;
  equipmentMakeId?: string;
}

export default function useEquipmentModels() {
  const client = useGraphQLClient();
  const org = useOrganization();

  const getEquipmentModels = async (filter?: IEquipmentModelFilter) => {
    try {
      const result = await client.request<EquipmentModelQuery, EquipmentModelQueryVariables>({
        document: EQUIPMENT_MODELS_QUERY,
        variables: {
          filter: {
            organizationId: { equal: org.id },
            deletedOn: { isNull: true },
            equipmentCategoryId: filter?.equipmentCategoryId ? { equal: filter.equipmentCategoryId } : undefined,
            equipmentMakeId: filter?.equipmentMakeId ? { equal: filter.equipmentMakeId } : undefined,
            type: filter?.equipmentModelType ? { equal: filter.equipmentModelType } : undefined,
          },
        },
      });
      return orderBy(result.equipmentModels, (cat) => cat.title?.toLowerCase()) as EquipmentModel[];
    } catch (error) {
      throw Error(`Error fetching equipment models: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const createEquipmentModel = async (createInput: EquipmentModelCreateInput) => {
    try {
      const result = await client.request<CreateEquipmentModelMutation, CreateEquipmentModelMutationVariables>({
        document: CREATE_EQUIPMENT_MODEL_MUTATION,
        variables: { equipmentModel: createInput },
      });

      const newModel = result.createEquipmentModel;

      if (!newModel) {
        throw Error('Failed to create equipment model');
      }

      return newModel;
    } catch (error) {
      throw Error(`Error creating equipment model: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const editEquipmentModel = async (editInput: EquipmentModelUpdateInput) => {
    try {
      const result = await client.request<UpdateEquipmentModelMutation, UpdateEquipmentModelMutationVariables>({
        document: UPDATE_EQUIPMENT_MODEL_MUTATION,
        variables: { equipmentModel: editInput },
      });

      const newModel = result.updateEquipmentModel;

      if (!newModel) {
        throw Error('Failed to update equipment model');
      }

      return newModel;
    } catch (error) {
      throw Error(`Error updating equipment model: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const deleteEquipmentModel = async (id: string) => {
    try {
      const result = await client.request<UpdateEquipmentModelMutation, UpdateEquipmentModelMutationVariables>({
        document: UPDATE_EQUIPMENT_MODEL_MUTATION,
        variables: { equipmentModel: { id, deletedOn: isoTimeStampUtc() } },
      });

      const newModel = result.updateEquipmentModel;

      if (!newModel) {
        throw Error('Failed to delete equipment model');
      }

      return newModel;
    } catch (error) {
      throw Error(`Error deleting equipment model: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return { getEquipmentModels, createEquipmentModel, editEquipmentModel, deleteEquipmentModel };
}

export const CREATE_EQUIPMENT_MODEL_MUTATION = gql`
  mutation CreateEquipmentModel($equipmentModel: EquipmentModelCreateInput) {
    createEquipmentModel(equipmentModel: $equipmentModel) {
      id
      title
      createdOn
      deletedOn
    }
  }
`;

export const UPDATE_EQUIPMENT_MODEL_MUTATION = gql`
  mutation UpdateEquipmentModel($equipmentModel: EquipmentModelUpdateInput) {
    updateEquipmentModel(equipmentModel: $equipmentModel) {
      id
      title
      updatedOn
      deletedOn
    }
  }
`;

export const EQUIPMENT_MODELS_QUERY = gql`
  query EquipmentModels($filter: EquipmentModelFilter, $sort: [EquipmentModelSort!]) {
    equipmentModels(filter: $filter, sort: $sort) {
      id
      organizationId
      equipmentMakeId
      equipmentCategoryId
      type
      title
      category {
        id
        title
      }
      make {
        id
        title
      }
      createdOn
      updatedOn
      deletedOn
      submittedOn
    }
  }
`;
